import classNames from 'classnames'
import { graphql, Link, PageProps } from 'gatsby'
import { intersection } from 'lodash'
import React, { useContext, useEffect } from 'react'

import Banner from '../../components/Banner'
import ClientBar from '../../components/ClientBar'
import ContactBanner from '../../components/ContactBanner'
import ContactForm from '../../components/ContactForm'
import ExpertContactCard from '../../components/ExpertContactCard'
import Metadata from '../../components/Metadata'
import Page from '../../components/Page'
import ProductCTABanner from '../../components/product/ctaBanner/ProductCTABanner'
import ProductCategoryTag from '../../components/product/ProductCategoryTag'
import ProductDeviceTypeTag from '../../components/product/ProductDeviceTypeTag'
import ProductStandardTag from '../../components/product/ProductStandardTag'
import QualitySystems from '../../components/product/QualitySystems'
import ProductWindow from '../../components/ProductWindow'
import Section from '../../components/section/Section'
import useProducts, { PlainProduct } from '../../hooks/useProducts'
import useTeamMember from '../../hooks/useTeamMember'
import { isHyphenatedLocale, LocaleContext } from '../../locale/i18n'
import {
  categoryLandingUrl,
  ISlugLink,
  staticPagePaths,
} from '../../locale/pathing'
import { Product } from '../../logic/catalog'
import {
  normalizeFormula,
  renderFormulas,
  renderLinks,
  renderText,
} from '../../logic/formatting'
import { ContentGroups, gaEvents } from '../../logic/ga4'
import { organizationMetadata } from '../../logic/ld-json'
import { nodeMappings, RichText } from '../../logic/richText'
import {
  CustomerTestimonial,
  IContentfulMethod,
  ProductFilterType,
  SiteMetadata,
  TeamMemberPageLink,
} from '../../logic/shared-types'
import { StaticPage } from '../../logic/staticPage'

import './product.scss'

export interface ProductPageContext {
  isDirectOrder: boolean
  localisedSlugs: ISlugLink[]
  productId: string
  productSlug: string
}

type ProductPageData = {
  methods: {
    nodes: Pick<
      IContentfulMethod,
      'acronym' | 'methodNameLong' | 'slug' | 'deviceTypeSlug'
    >[]
  }
  teamMemberPageLinks: {
    nodes: TeamMemberPageLink[]
  }
  site: { siteMetadata: Pick<SiteMetadata, 'siteUrl'> }
  testimonials: {
    nodes: CustomerTestimonial[]
  }
  products: {
    nodes: { productData: Product }[]
  }
}

const renderDescription = renderText(renderLinks, renderFormulas)

const similarityTags = ({
  deviceTypes,
  industryOrMaterial,
  standards,
  typeOfTesting,
}: PlainProduct) => {
  return [
    ...deviceTypes,
    ...industryOrMaterial,
    ...typeOfTesting,
    ...standards.map(s => s.id),
  ]
}

const similarityScore = (a: PlainProduct, b: PlainProduct) => {
  return intersection(similarityTags(a), similarityTags(b)).length
}

const ProductPage = ({
  data: {
    methods,
    site: {
      siteMetadata: { siteUrl },
    },
    testimonials: { nodes: testimonials },
    products: {
      nodes: [productNode],
    },
  },
  location,
  pageContext: { isDirectOrder, localisedSlugs, productId },
}: PageProps<ProductPageData, ProductPageContext>) => {
  if (!productNode) {
    throw new Error(`Product ${productId} not found`)
  }
  const product = productNode.productData
  const { locale, t } = useContext(LocaleContext)
  const products = useProducts()
  const similarProducts = products
    .map(p => ({
      ...p,
      similarity: similarityScore(p, product),
    }))
    .filter(p => p.similarity > 0 && p.id !== productId)
    .sort((a, b) => b.similarity - a.similarity)
    .slice(0, 10)

  // Select a pseudo-random but deterministic testimonial by summing product uuid
  // Sum has to match on backend and frontend render for hydration
  const uuidSum = productId
    .split('-')
    .reduce((acc, val) => acc + parseInt(val, 16), 0)
  const testimonial = testimonials[uuidSum % testimonials.length]

  const {
    contactFormInstructions,
    description,
    descriptionRaw,
    detectionLimit,
    deviceTypes,
    estimatedPrice,
    fixedUnitPriceCents,
    id,
    industryOrMaterial,
    metaDescription,
    owner,
    sampleRequirements,
    standards,
    suitableSampleMaterials,
    tatParameter,
    turnaroundWeeks,
    typeOfTesting,
  } = product

  const name = normalizeFormula(product.name)
  const metaTitle = normalizeFormula(product.metaTitle)

  const teamMember = useTeamMember(owner.email)
  useEffect(() => {
    if (!isDirectOrder) {
      gaEvents.productDetailsViewed(product)
    }
  }, [])

  const maybeTableField: (
    label: string,
    rawValue: React.ReactNode,
    valueRenderer?: () => React.ReactNode,
  ) => { label: string; value: React.ReactNode }[] = (
    label,
    rawValue,
    valueRenderer,
  ) =>
    rawValue
      ? [{ label, value: valueRenderer ? valueRenderer() : rawValue }]
      : []

  const turnaroundTimeOptions = [
    turnaroundWeeks.max,
    turnaroundWeeks.min,
    ...(tatParameter?.options.flatMap(opt => [
      opt.turnaroundWeeks.max,
      opt.turnaroundWeeks.min,
    ]) ?? []),
  ].filter((x): x is number => x !== null)

  const formattedTatField = () => {
    if (typeof turnaroundTimeOptions[0] === 'undefined') {
      return '-'
    }
    // Show range of turnaround times or just the single value
    return `${
      turnaroundTimeOptions.length > 1
        ? `${Math.min(...turnaroundTimeOptions)} – ${Math.max(
            ...turnaroundTimeOptions,
          )}`
        : turnaroundTimeOptions[0]
    } ${
      turnaroundTimeOptions.length > 1 || turnaroundTimeOptions[0] > 1
        ? t('productDetails.turnaroundWeeksPlural')
        : t('productDetails.turnaroundWeeksSingular')
    }`
  }

  const tableData: { label: string; value: React.ReactNode }[] = [
    ...maybeTableField(
      t('productDetails.sampleMatrices'),
      suitableSampleMaterials,
      () => renderFormulas(suitableSampleMaterials),
    ),
    ...maybeTableField(
      t('productDetails.sampleRequirements'),
      sampleRequirements,
      () => renderFormulas(sampleRequirements),
    ),
    ...(turnaroundTimeOptions.length > 0
      ? [
          {
            label: t('productDetails.turnaround'),
            value: formattedTatField(),
          },
        ]
      : []),
    ...maybeTableField(t('productDetails.detectionLimit'), detectionLimit, () =>
      renderFormulas(detectionLimit),
    ),
    {
      label: t('productDetails.qualitySystems'),
      value: <QualitySystems product={product} />,
    },
    ...(deviceTypes?.length
      ? [
          {
            label: t('productDetails.deviceTypes'),
            value: (
              <div className="tags">
                {deviceTypes.map(d => (
                  <ProductDeviceTypeTag
                    deviceType={d}
                    href={`${
                      staticPagePaths[StaticPage.PRODUCTS][locale]
                    }?type=${encodeURIComponent(d.deviceTypeId)}`}
                    key={d.deviceTypeId}
                  />
                ))}
              </div>
            ),
          },
        ]
      : []),
    ...(standards.length
      ? [
          {
            label: t('productDetails.standard'),
            value: (
              <div className="tags">
                {standards.map(s => (
                  <ProductStandardTag
                    href={`${
                      staticPagePaths[StaticPage.PRODUCTS][locale]
                    }?standard=${encodeURIComponent(s.slug)}`}
                    standard={s}
                    key={s.id}
                  />
                ))}
              </div>
            ),
          },
        ]
      : []),
    ...(teamMember?.isActive
      ? maybeTableField(t('productDetails.expert'), owner.email, () => (
          <ExpertContactCard email={owner.email} />
        ))
      : []),
  ]

  const absoluteProductUrl = siteUrl + location.pathname

  const landingPageLinks = methods.nodes.filter(m =>
    product.deviceTypes.some(
      dt => m.deviceTypeSlug !== null && dt.deviceTypeId === m.deviceTypeSlug,
    ),
  )

  return (
    <Page
      contentGroup={ContentGroups.productPage}
      localisedSlugs={localisedSlugs}
      breadcrumbParts={[
        {
          internalLink: staticPagePaths[StaticPage.PRODUCTS][locale],
          text: t('productDetails.breadcrumbRoot'),
        },
        {
          internalLink: location.pathname,
          text: name || '',
        },
      ]}
      siteUrl={siteUrl}
    >
      <Metadata
        title={metaTitle || name}
        description={metaDescription || descriptionRaw || ''}
      >
        <script type="application/ld+json">
          {JSON.stringify({
            '@context': 'https://schema.org/',
            '@type': 'Product',
            brand: organizationMetadata,
            description: descriptionRaw,
            image: industryOrMaterial[0]
              ? `https://measurlabs.com/category-icons/${industryOrMaterial[0]}.svg`
              : `https://measurlabs.com/measurlabs-badge.svg`,
            name,
            sku: String(id),
            url: absoluteProductUrl,
            ...(industryOrMaterial[0]
              ? { category: t(`industryOrMaterial.${industryOrMaterial[0]}`) }
              : {}),
            ...(estimatedPrice !== null
              ? {
                  offers: Object.entries(estimatedPrice.minCents).map(
                    ([currency, cents]) => ({
                      '@type': 'Offer',
                      availability: 'https://schema.org/InStock',
                      name: name ? `${name}: estimate` : 'Estimate',
                      offeredBy: organizationMetadata,
                      price: cents / 100,
                      priceCurrency: currency,
                      url: absoluteProductUrl,
                    }),
                  ),
                }
              : fixedUnitPriceCents !== null
                ? {
                    offers: Object.entries(fixedUnitPriceCents).map(
                      ([currency, cents]) => ({
                        '@type': 'Offer',
                        availability: 'https://schema.org/InStock',
                        name: name ? `${name}: first sample` : 'First sample',
                        offeredBy: organizationMetadata,
                        price: cents / 100,
                        priceCurrency: currency,
                        url: absoluteProductUrl,
                      }),
                    ),
                  }
                : {}),
          })}
        </script>
      </Metadata>
      <div className="gutter gutter--center">
        <div className="productPage">
          <div className="productPage__product">
            <div className="productPage__productInfo">
              <h1 className="productPage__heading">{name}</h1>
              {description && (
                <RichText renderText={renderDescription}>
                  {description}
                </RichText>
              )}
              {landingPageLinks.length > 0 && (
                <div className="productPage__landingLinks">
                  <h3 className="productPage__landingLinksTitle">
                    {t('productDetails.landingPageLink')}:
                  </h3>
                  {landingPageLinks.map(({ acronym, methodNameLong, slug }) => (
                    <Link
                      className="productPage__landingLink"
                      key={slug}
                      to={
                        [
                          staticPagePaths[StaticPage.METHODS][locale],
                          slug,
                        ].join('') + '/'
                      }
                    >
                      {acronym
                        ? `${methodNameLong} (${acronym})`
                        : methodNameLong}
                    </Link>
                  ))}
                </div>
              )}
              {tableData.length > 0 && (
                <dl className="productPage__table">
                  {tableData.map(({ label, value }) => (
                    <div className="productPage__tableRow" key={label}>
                      <dt
                        className={classNames({
                          hyphenate: isHyphenatedLocale(),
                        })}
                      >
                        {label}
                      </dt>
                      <dd>{value}</dd>
                    </div>
                  ))}
                </dl>
              )}
              <div className="tags productPage__categories">
                {industryOrMaterial.map(pc => (
                  <ProductCategoryTag
                    dimension={ProductFilterType.INDUSTRY_OR_MATERIAL}
                    key={pc}
                    category={pc}
                    href={categoryLandingUrl(locale, pc)}
                  />
                ))}
                {typeOfTesting.map(pc => (
                  <ProductCategoryTag
                    dimension={ProductFilterType.TYPE_OF_TESTING}
                    key={pc}
                    category={pc}
                    href={categoryLandingUrl(locale, pc)}
                  />
                ))}
              </div>
            </div>
          </div>
          <div className="productPage__sideWrapper top-24">
            <ProductCTABanner product={product} />
            <ContactBanner
              className="productPage__contactBannerCompact"
              compact
            />
            <ContactBanner className="productPage__contactBanner" />
          </div>
        </div>
        {similarProducts.length > 0 && (
          <div className="productPage__related">
            <h2 className="productPage__heading">
              {t('productPage.otherTests')}
            </h2>
            <ProductWindow className="mt-6" products={similarProducts} />
          </div>
        )}
        <ClientBar />
        <br />
      </div>
      {testimonial && (
        <Banner
          firstLine={`”${testimonial.testimonial.testimonial}”`}
          subLine={
            <div>
              <strong>{testimonial.customerName}</strong>,{' '}
              {[testimonial.customerTitle, testimonial.customerCompany]
                .filter(s => s)
                .join(', ')}
            </div>
          }
        />
      )}
      <Section backgroundColor="light" id="askForOffer">
        <ContactForm
          instructions={
            contactFormInstructions ? (
              <RichText
                nodes={{
                  ...nodeMappings,
                  doc: props => <React.Fragment {...props} />,
                }}
              >
                {contactFormInstructions}
              </RichText>
            ) : null
          }
        />
      </Section>
    </Page>
  )
}

export const query = graphql`
  query Product($locale: String, $productId: String) {
    methods: allContentfulMethodPage(filter: { node_locale: { eq: $locale } }) {
      nodes {
        acronym
        methodNameLong
        deviceTypeSlug
        slug
      }
    }
    site {
      siteMetadata {
        siteUrl
      }
    }
    teamMemberPageLinks: allContentfulTeamMemberPage {
      nodes {
        ...TeamMemberPages
      }
    }
    testimonials: allContentfulCustomerTestimonial(
      filter: {
        node_locale: { eq: $locale }
        testimonial: { testimonial: { ne: null } }
      }
    ) {
      nodes {
        ...TestimonialParts
      }
    }
    products: allProduct(
      filter: {
        locale: { eq: $locale }
        productData: { id: { eq: $productId } }
      }
    ) {
      nodes {
        productData {
          accreditation
          contactFormInstructions
          description
          descriptionRaw
          detectionLimit
          deviceTypes {
            deviceTypeId
            parentDeviceTypeId
            nameLong
            nameShort
          }
          employeeId
          estimatedPrice {
            maxCents {
              EUR
              GBP
              USD
            }
            minCents {
              EUR
              GBP
              USD
            }
          }
          fixedUnitPriceCents {
            EUR
            GBP
            USD
          }
          id
          industryOrMaterial
          metaDescription
          metaTitle
          name
          owner {
            email
            name
          }
          parameters {
            helperText
            id
            slug
            options {
              id
              displayName
              priceDeltaCents {
                EUR
                GBP
                USD
              }
            }
            title
          }
          pipedriveProductId
          popularityRank
          qualitySystems
          sampleRequirements
          slug
          standards {
            code
            group
            id
            slug
          }
          suitableSampleMaterials
          tatParameter {
            helperText
            id
            slug
            options {
              id
              displayName
              priceDeltaCents {
                EUR
                GBP
                USD
              }
              turnaroundWeeks {
                max
                min
              }
            }
            title
          }
          turnaroundWeeks {
            max
            min
          }
          typeOfTesting
          webshopEnabled
          websitePricingModel
        }
      }
    }
  }
`

export default ProductPage
